// ─── Crash Reporter (debug instrumentation — PASO 1) ──────────────────────────
//
// Purpose: surface uncaught exceptions ON-SCREEN so they can be read directly on
// the device (e.g. the Motorola Android phone where the run freezes) without USB,
// remote debugging, or a desktop console.
//
// It never swallows errors: every call logs the full stack to console.error, and
// the on-screen panel shows the first error verbatim. This is a temporary
// debugging aid for the mobile-only freeze; gate or remove it once fixed.

let overlayShown = false;

/**
 * Logs the error (always) and renders a scrollable, copyable on-screen panel with
 * the message + full stack. Only the first error renders a panel to avoid stacking
 * overlays every frame; console.error still fires for every call (never silent).
 */
export function showCrashOverlay(context: string, error: unknown): void {
  // Always log the full stack — never silent, never masked.
  console.error(`[${context}] Excepción no capturada:`, error);

  if (overlayShown || typeof document === "undefined") return;
  overlayShown = true;

  const err = error as { message?: string; stack?: string } | undefined;
  const message = err?.message ?? String(error);
  const stack = err?.stack ?? "(sin stack)";

  const panel = document.createElement("div");
  panel.id = "crash-overlay";
  panel.setAttribute(
    "style",
    [
      "position:fixed",
      "inset:0",
      "z-index:2147483647",
      "background:rgba(5,7,8,0.94)",
      "color:#dfff2f",
      "font-family:Consolas,monospace",
      "font-size:12px",
      "line-height:1.45",
      "padding:16px",
      "overflow:auto",
      "white-space:pre-wrap",
      "word-break:break-word",
      "-webkit-user-select:text",
      "user-select:text"
    ].join(";")
  );

  const title = document.createElement("div");
  title.setAttribute("style", "color:#ffdd66;font-weight:900;font-size:14px;margin-bottom:10px");
  title.textContent = `CRASH — ${context}`;

  const body = document.createElement("div");
  body.textContent = `${message}\n\n${stack}`;

  const close = document.createElement("button");
  close.textContent = "Cerrar";
  close.setAttribute(
    "style",
    "margin-top:16px;padding:8px 16px;background:#1b2a18;color:#dfff2f;border:1px solid #b7ff00;border-radius:6px;font-family:inherit;font-size:13px"
  );
  close.addEventListener("click", () => {
    panel.remove();
    overlayShown = false;
  });

  panel.append(title, body, close);
  document.body.appendChild(panel);
}

/**
 * Catches anything that escapes the game's own try/catch (e.g. errors thrown from
 * async startup or outside the update loop) and routes them to the same on-screen
 * panel. Low-risk, additive, and only ever visible when a real error occurs.
 */
export function installGlobalErrorOverlay(): void {
  if (typeof window === "undefined") return;
  window.addEventListener("error", (event) => {
    showCrashOverlay("window.error", event.error ?? event.message);
  });
  window.addEventListener("unhandledrejection", (event) => {
    showCrashOverlay("unhandledrejection", event.reason);
  });
}

/**
 * Optional on-screen DevTools console for mobile, loaded ONLY when the URL has
 * `?eruda=1`. Never activates during normal play.
 */
export function loadErudaIfRequested(): void {
  if (typeof window === "undefined" || typeof document === "undefined") return;
  if (new URLSearchParams(window.location.search).get("eruda") !== "1") return;

  const script = document.createElement("script");
  script.src = "https://cdn.jsdelivr.net/npm/eruda";
  script.onload = () => {
    (window as unknown as { eruda?: { init: () => void } }).eruda?.init();
  };
  document.body.appendChild(script);
}
